<template>
	<nav class="stream-navigation-sidebar">
		<component v-for="n in navigationItems"
			:is="n.component"
			:key="n.uiKey"
			:date="n.date"
			:label="n.label"
			:recurrence-id="n.recurrenceId"
			:is-active="n.isActive"
			@click="onNavItemClicked(n)"
		/>
	</nav>
</template>

<script>

import {
	LoadingPlaceholder2
} from "@teaming/common-ui";

import StreamNavigationCard from "./StreamNavigationCard.vue";
import StreamNavigationHeading from "./StreamNavigationHeading.vue";

const componentMap = {
	"heading:week": "StreamNavigationHeading",
	entry: "StreamNavigationCard",
};

export default {
	name: "StreamNavigationSidebar",
	teamingPackage: "common-team",
	emits: [ "nav", ],
	components: {
		LoadingPlaceholder2,
		StreamNavigationCard,
		StreamNavigationHeading,
	},
	props: {
		documentName: {
			type: String,
			required: true,
		},
		noteHistory: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			activeSubDocId: "",
		};
	},
	computed: {
		navigationItems() {
			return this.noteHistory
				.filter( entry => entry.uiDiscriminator !== "heading:month" )
				.map( entry => {
					const component = componentMap[ entry.uiDiscriminator ];

					const {
						subDocId,
						uiKey,
						titleLabel,
						dateLabel,
						recurrenceId,
					} = entry;

					return {
						subDocId,
						uiKey: `stream-navigation-sidebar-${ uiKey }`,
						component,
						label: titleLabel,
						recurrenceId,
						date: dateLabel,
						isActive: ( subDocId === this.activeSubDocId ),
					};
				} );
		},
	},
	methods: {
		async onEditorActivated( subDocId ) {
			await this.$nextTick();
			this.activeSubDocId = subDocId;
		},
		createListeners() {
			const { documentName, } = this;
			const registry = this.$documentRegistry();
			registry.on( `${ documentName }.editorActivated`, this.onEditorActivated );
		},
		disposeListeners() {
			const { documentName, } = this;
			const registry = this.$documentRegistry();
			registry.off( `${ documentName }.editorActivated`, this.onEditorActivated );
		},
		trySyncWithRegistry() {
			const registry = this.$documentRegistry();
			if ( !registry.isConnected( this.documentName ) ) {
				return;
			}

			// if this is being re-mounted after the document registry has
			// been set up, there may already be an active subDoc ID, so
			// attempt to get it
			const manager = registry.manage( this.documentName );
			const subDoc = manager.getActiveSubDoc();
			if ( !subDoc ) {
				return;
			}
			this.onEditorActivated( subDoc.subDocId );
		},
		onNavItemClicked( navItem ) {
			this.activeSubDocId = navItem.subDocId;
			this.$emit( "nav", navItem );
		},
	},
	async mounted() {
		this.createListeners();
		await this.$nextTick();
		this.trySyncWithRegistry();
	},
	beforeUnmount() {
		this.disposeListeners();
	},
};
</script>

<style lang="scss" scoped>
$emptyPlaceholderTextColor: $color1-700;
$emptyPlaceholderBackgroundColor: $color1-050;

.stream-navigation-sidebar {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;
}

.stream-navigation-sidebar--loading,
.stream-navigation-sidebar--empty {
	justify-content: center;
	height: 100%;
}

.empty-placeholder {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	height: 100%;
	border-radius: 0.5rem;
	color: $emptyPlaceholderTextColor;
	background-color: $emptyPlaceholderBackgroundColor;
}
</style>
